import Vue from 'vue';
import { omit } from 'lodash';
import { removableWord } from '@/utils/main';
import { AutocompleteChipsTypes } from '@/utils/classes/AutocompleteChipsTypes';
import Moment from 'moment-timezone';
import * as types from './mutation-types';


/**
 * Set proper problem properties to be editable
 * @param  {Object} problem - new problem object
 * @param  {Object} oldProblem - previous value of the problem data
 * @return {Object} - the updated problem with all properties needed
 */
function setupProblem(problem, oldProblem = {}) {
  if (problem.keywords !== undefined && problem.keywords instanceof Array) {
    let html = '';
    for (let i = 0, len = problem.keywords.length; i < len; i++) {
      html += removableWord(problem.keywords[i].word, problem.keywords[i].id, AutocompleteChipsTypes.KEYWORD);
    }
    problem.editableKeywords = html;
  }
  problem.pictures = oldProblem.pictures || [];
  return { ...oldProblem, ...problem };
}


const mutations = {

  /** Save problems using the filter configuration */
  [types.SET_PROBLEMS](state, { payload, data }) {
    const stateKey = JSON.stringify(omit(payload, 'limit'));
    const stateContent = state.problemsFetchConfigurations[stateKey] || { problemIds: [] };
    const newProblemIds = data.map(problem => problem.id);
    stateContent.problemIds = [
      ...stateContent.problemIds.filter(id => !newProblemIds.includes(id)),
      ...newProblemIds,
    ];
    Vue.set(state.problemsFetchConfigurations, stateKey, stateContent);
  },

  [types.SET_PROBLEM](state, payload) {
    let key;
    let newProblem;
    if (payload.problem) {
      key = `${payload.problem.id}`;
      newProblem = payload.problem;
    } else if (payload.id) {
      key = `${payload.id}`;
      newProblem = payload;
    }
    Vue.set(state.problems, key, setupProblem(newProblem, state.problems[key]));
  },

  /** Save actions using the filter configuration */
  [types.SET_ACTIONS](state, { payload, data }) {
    const stateKey = JSON.stringify(omit(payload, 'limit'));
    const stateContent = state.actionsFetchConfigurations[stateKey] || { actionIds: [] };
    const newActionIds = data.map(action => action.id);
    stateContent.actionIds = [
      ...stateContent.actionIds.filter(id => !newActionIds.includes(id)),
      ...newActionIds,
    ];
    Vue.set(state.actionsFetchConfigurations, stateKey, stateContent);
  },

  [types.SET_ACTION](state, payload) {
    const prevAction = state.actions[`${payload.id}`];
    Vue.set(state.actions, `${payload.id}`, { ...prevAction, ...payload });
  },

  [types.DELETE_PROBLEM](state, payload) {
    // Remove the id from any fetch configuration
    const problemId = payload.id;
    Object.keys(state.problemsFetchConfigurations).forEach((cfgKey) => {
      const stateContent = state.problemsFetchConfigurations[cfgKey];
      if (stateContent && stateContent.problemIds) {
        stateContent.problemIds = stateContent.problemIds.filter(id => id !== problemId);
      }
    });
  },

  [types.SET_PROBLEM_PICTURES](state, payload) {
    if (payload.id && state.problems[`${payload.id}`]) {
      Vue.set(state.problems[`${payload.id}`], 'pictures', payload.data || []);
      Vue.set(state.problems[`${payload.id}`], 'pictsLoaded', true);
    }
  },

  [types.SET_CAUSE_PICTURES](state, payload) {
    if (payload.cause) {
      state.causePictures[`${payload.cause}`].pictures = payload.data || [];
      state.causePictures[`${payload.cause}`].pictsLoaded = true;
      state.causePictures[`${payload.cause}`].loading = false;
    }
  },

  [types.SET_CAUSE_PICTURES_LOADING](state, payload) {
    if (payload.cause) {
      const isLoading = payload.loading === true;
      if (state.causePictures[`${payload.cause}`]) {
        state.causePictures[`${payload.cause}`].loading = isLoading;
      } else {
        Vue.set(state.causePictures, `${payload.cause}`, { loading: isLoading, pictures: '' });
      }
    }
  },

  [types.SET_PROBLEM_HISTORY](state, payload) {
    if (payload.problemId) {
      Vue.set(state.history, `${payload.problemId}`, payload.data);
      Vue.set(state.historyLoading, `${payload.problemId}`, false);
    }
  },

  [types.SET_HISTORY_LOADING](state, payload) {
    Vue.set(state.historyLoading, `${payload.problemId || 0}`, payload.loading === true);
  },

  // Following mutations are created to identify the infinite problem creation loop issue
  [types.LOG_CLB](state, payload) {
    if (typeof payload === 'string') {
      state.creationLogBuffer = `${state.creationLogBuffer}<br>\
        ${Moment().format('YYYY-MM-DD HH:mm:ss')} -=- ${payload}`;
    }
  },

  [types.INCREMENT_ADD_PROBLEM_CALLS](state) {
    state.latestProblemCalls.push(Moment());
  },

  // Following mutations are created to identify the infinite problem creation loop issue
  [types.PB_INTERRUPTION_MODAL_DISPLAYED](state, payload) {
    if (payload === true || payload === false) {
      state.problemInterruptionModalDisplayed = payload;
    }
  },

  // Following mutations are created to identify the infinite problem creation loop issue
  [types.RESUME_PROBLEM_CREATION](state, payload) {
    if (payload === true || payload === false) {
      state.interruptProblemCreation = !payload;
      Vue.set(state, 'latestProblemCalls', []);
    }
  },

  // Edit the accesses of a problem
  [types.SET_PROBLEM_ACCESSES](state, payload) {
    if (payload.id > 0 && payload.accesses instanceof Array) {
      const { id, accesses } = payload;
      Vue.set(state.problems, `${id}`, { ...state.problems[`${id}`], accesses });
    }
  },

  [types.ADD_ACTION](state, payload) {
    const problem = state.problems[`${payload.problem}`];
    if (problem) {
      if (problem.actions) {
        problem.actions.push(payload);
        Vue.set(problem, 'actions', problem.actions);
      } else {
        Vue.set(problem, 'actions', [payload]);
      }
    }
  },

  [types.DELETE_ACTION](state, payload) {
    const problem = state.problems[`${payload.problemId}`];
    if (problem && problem.actions) {
      problem.actions.splice(problem.actions.findIndex(action => action.id === payload.id), 1);
      Vue.set(problem, 'actions', problem.actions);
    }
  },

  [types.EDIT_ACTION](state, payload) {
    const problem = state.problems[`${payload.problemId}`];
    if (problem && problem.actions) {
      problem.actions.splice(problem.actions.findIndex(action => action.id === payload.id), 1, payload);
      Vue.set(problem, 'actions', problem.actions);
    }
  },
};

export default mutations;
